import React, { useState } from 'react';
import { Button, Card, Alert, Spinner, Badge, Form, Modal } from 'react-bootstrap';
import { useData } from '../Data/DataContext';
import {Navigate, useNavigate, useParams} from 'react-router-dom';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import * as Yup from 'yup';
import { Formik, Form as FormikForm, Field, ErrorMessage, FormikHelpers } from 'formik';
import axiosInstance from '../../utils/axiosConfig';
import CustomContainer from '../Utils/CustomContainer';
import translateCreateOrUpdateSickLeave from './errorMessages';
import { sickLeaveTranslations } from './translations';
import {IClient, IClientSickLeave, IEmployeeSickLeave} from "../../utils/FrontendDataTypes";

// Definition der für die Bearbeitung benötigten Typen
interface ISickLeave {
  recordId: number,
  type: 'client'|'employee',
  from: Date,
  to?: Date,
  client?: IClient,
  reason?: number,
  eAuFollows?: boolean,
  sickLeaveNote?: string,
}

interface FormData {
  toDate: string,
  eAuFollows: boolean,
  sickNoteFile?: File | undefined,
}

const EditSingleSickLeave: React.FC = () => {
  const { data, loading, error, refreshData } = useData();
  const navigate = useNavigate();
  const { id, type } = useParams<{ id: string, type: string }>(); // Holt die ID und den Typ aus der URL

  const [updateSuccess, setUpdateSuccess] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<{
    title: string,
    message: string,
    type: string,
    secondMessage: string | null,
    warning: boolean
  }|null>(null);
  const [currentSickLeave, setCurrentSickLeave] = useState<{sickLeave: ISickLeave, formData: FormData} | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [finishClientSickLeave, setFinishClientSickLeave] = useState<boolean>(false);

  // Konstanten für Gründe der Krankmeldung
  const sickLeaveReasonEmployeeSick = 1;
  const sickLeaveReasonChildOfEmployeeSick = 2;

  // Validierungsschema für das Formular
  const getValidationSchema = (sickLeave: ISickLeave | null) => {
    let vs = Yup.object({
      toDate: Yup.string()
          .required('Das Enddatum ist erforderlich.')
          .test(
              'is-valid-to-date',
              'Das Enddatum muss mindestens dem Startdatum entsprechen.',
              function (value) {
                if (!value) return true;
                if (!sickLeave) return true;

                const toDate = new Date(value);
                const fromDate = sickLeave.from;
                if (!fromDate) return false;

                // Beide Daten auf den gleichen Zeitpunkt setzen (0:00 Uhr)
                const fromDateOnly = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
                const toDateOnly = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());

                // Datum muss mindestens gleich sein
                return toDateOnly >= fromDateOnly;
              }
          ),
    });

    if (isEmployeeSick()) {
      vs = vs.concat(Yup.object({
        eAuFollows: Yup.boolean().required(),
        sickNoteFile: Yup.mixed()
            .when('eAuFollows', {
              is: false,
              then: (schema) => schema.required('Bitte laden Sie eine AU-Meldung hoch.'),
              otherwise: (schema) => schema.optional()
            })
      }));
    }

    return vs;
  };

  // Funktion zum Konvertieren einer IEmployeeSickLeave in das ISickLeave Format
  const convertEmployeeSickLeave = (sickLeave: IEmployeeSickLeave): ISickLeave => {
    return {
      recordId: sickLeave.record_id,
      type: 'employee',
      from: new Date(sickLeave.from),
      to: sickLeave.to ? new Date(sickLeave.to) : undefined,
      reason: sickLeave.reason,
      eAuFollows: sickLeave.e_au_follows,
      sickLeaveNote: sickLeave.proof_document
    };
  };

  // Funktion zum Konvertieren einer IClientSickLeave in das ISickLeave Format
  const convertClientSickLeave = (sickLeave: IClientSickLeave): ISickLeave => {
    const client = data?.clients.find(c => c.record_id === sickLeave.client);

    return {
      recordId: sickLeave.record_id,
      type: 'client',
      from: new Date(sickLeave.from),
      to: sickLeave.to ? new Date(sickLeave.to) : undefined,
      client: client as IClient
    };
  };

  // Funktion zum Speichern der Krankmeldung
  const confirmSubmit = () => {
    if (!currentSickLeave || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    const { sickLeave, formData } = currentSickLeave;

    const sickLeaveType = (() : string => {
      if (sickLeave.type === 'client') {
        return 'client_sick'
      }
      if (sickLeave.type === 'employee') {
        if (sickLeave.reason === 1) {
          return 'employee_is_sick'
        }
        if (sickLeave.reason === 2) {
          return 'child_of_employee_is_sick'
        }
        throw new Error('failed to resolve reason')
      }
      throw new Error('invalid sick leave type')
    })()

    const reqFormData = new FormData();
    reqFormData.append('record_id', sickLeave.recordId.toString());
    reqFormData.append('eau', formData.eAuFollows.toString());

    reqFormData.append('sick_leave_type', sickLeaveType);
    reqFormData.append('from', format(sickLeave.from, 'yyyy-MM-dd'))

    if (formData.toDate) {
      reqFormData.append('to', formData.toDate);
    }

    if (formData.sickNoteFile) {
      reqFormData.append('sick_note_file', formData.sickNoteFile);
    }

    // in case of finishing client sick leave
    if (finishClientSickLeave && sickLeave.type === 'client') {
      reqFormData.append('finished_by_employee', 'true')
    }

    axiosInstance
      .post('/create-or-update-sick-leave', reqFormData)
      .then(() => {
        setIsSubmitting(false);
        setShowConfirmModal(null);
        setCurrentSickLeave(null);
        handleSickLeaveUpdated();
      })
      .catch((error) => {
        setIsSubmitting(false);
        setShowConfirmModal(null);

        // Extrahiere Fehlercode aus der API-Antwort
        const errorCode = error.response?.data?.code;
        const translatedError = translateCreateOrUpdateSickLeave(errorCode);

        setErrorMessage(translatedError || 'Fehler beim Senden der Krankmeldung. Bitte versuchen Sie es später erneut.');
        console.error('Fehler beim Senden der Krankmeldung:', error);
      });
  };

  // Callback nach erfolgreicher Speicherung einer Krankmeldung
  const handleSickLeaveUpdated = () => {
    setUpdateSuccess(true);
    // @todo .then .catch
    refreshData();

    // Nach 3 Sekunden die Erfolgsmeldung ausblenden
    setTimeout(() => {
      setUpdateSuccess(false);
    }, 3000);
  };

  // Finde die Krankmeldung basierend auf der URL-Parameter
  const findSickLeave = () : {sickLeave: ISickLeave|null, sickLeaveFinished: boolean} => {
    const emptyReturn = {sickLeave: null, sickLeaveFinished: false}
    if (!data || !id) {
      return {sickLeave: null, sickLeaveFinished: false};
    }

    const sickLeaveId = parseInt(id);

    if (type === 'employee') {
      const foundSickLeave = data.employee_sick_leave.find(sl => sl.record_id === sickLeaveId);
      if (!foundSickLeave) {
        return emptyReturn;
      }
      const transformedFoundSickLeave = convertEmployeeSickLeave(foundSickLeave);
      return {
        sickLeave: transformedFoundSickLeave,
        sickLeaveFinished: foundSickLeave.employee_submitted_data,
      }
    } else if (type === 'client') {
      const foundSickLeave = data.client_sick_leave.find(sl => sl.record_id === sickLeaveId);
      return {
        sickLeave: foundSickLeave ? convertClientSickLeave(foundSickLeave) : null,
        // @todo change this once finishing logic is implemented
        sickLeaveFinished: false
      }
    }

    return emptyReturn;
  };

  // Extrahiere die Krankmeldung
  const {sickLeave, sickLeaveFinished} = findSickLeave();

  // Finde den entsprechenden Originaldatensatz für zusätzliche Informationen
  const originalSickLeave = type === 'employee' 
    ? data?.employee_sick_leave.find(sl => sl.record_id === parseInt(id || '0')) 
    : null;

  if (loading) {
    return (
      <CustomContainer>
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{sickLeaveTranslations.loading.text}</span>
          </Spinner>
          <p className="mt-3">{sickLeaveTranslations.loading.message}</p>
        </div>
      </CustomContainer>
    );
  }

  if (error) {
    return (
      <CustomContainer>
        <Alert variant="danger">
          {error}
          <Button
            variant="outline-danger"
            size="sm"
            className="ms-3"
            onClick={() => navigate('/dashboard')}
          >
            Zurück zum Dashboard
          </Button>
        </Alert>
      </CustomContainer>
    );
  }

  if (!sickLeave || sickLeaveFinished) {
    return <Navigate to={"/dashboard"} replace={true} />
  }

  // Initialisiere die Formularwerte
  const initialValues: FormData = {
    toDate: sickLeave.to ? format(sickLeave.to, 'yyyy-MM-dd') : '',
    eAuFollows: sickLeave.eAuFollows ?? false
  };

  const isEmployeeSick = () : boolean => {
    return sickLeave.type === 'employee' && sickLeave.reason === 1
  }

  return (
    <CustomContainer>
      <h1 className="mb-4">{sickLeaveTranslations.page.title(sickLeave.type, isEmployeeSick())}</h1>

      {updateSuccess && (
        <Alert variant="success" dismissible onClose={() => setUpdateSuccess(false)}>
          {sickLeaveTranslations.page.successMessage(sickLeave.type)}
        </Alert>
      )}

      {errorMessage && (
        <Alert variant="danger" dismissible onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      <div className="d-flex justify-content-start mb-4">
        <Button
          variant="outline-secondary"
          onClick={() => navigate('/sick-leave')}
        >
          <i className="fas fa-arrow-left me-2"></i> {sickLeaveTranslations.page.backButton}
        </Button>
      </div>

      <Card className="shadow-sm sick-leave-card mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center bg-primary text-white">
          <div>{sickLeaveTranslations.sickLeaveType(sickLeave.type, isEmployeeSick())}</div>
          {(sickLeave.type === 'employee' && !originalSickLeave?.employee_submitted_data) || 
           (sickLeave.type === 'client' && !sickLeave.to) ? (
            <Badge bg="warning">{sickLeaveTranslations.status.open}</Badge>
          ) : null}
        </Card.Header>
        <Card.Body>
          <Card.Title>
            {sickLeaveTranslations.title(sickLeave.type, isEmployeeSick(), format(sickLeave.from, 'EEEE, d. MMMM yyyy', { locale: de }), sickLeave.client?.first_name, sickLeave.client?.last_name)}
          </Card.Title>

          <div className="alert alert-secondary my-3 py-2">
            <small>
              {sickLeaveTranslations.message(
                  sickLeave.type, isEmployeeSick(),
                  format(sickLeave.from, 'd. MMMM', { locale: de }),
                  sickLeave.client?.first_name || '',
                  sickLeave.client?.last_name || '',
              )}
            </small>
          </div>

          <Formik
            validationSchema={getValidationSchema(sickLeave)}
            initialValues={initialValues}
            validateOnBlur={true}
            validateOnChange={true}
            onSubmit={(values: FormData, actions: FormikHelpers<FormData>) => {
              setCurrentSickLeave({
                sickLeave: sickLeave,
                formData: values,
              });
              if (finishClientSickLeave) {
                setShowConfirmModal({
                  title: sickLeaveTranslations.modal.finish.title,
                  message: sickLeaveTranslations.modal.finish.message,
                  type: 'warning',
                  secondMessage: sickLeaveTranslations.modal.finish.secondMessage,
                  warning: true
                });
              } else {
                setShowConfirmModal({
                  title: sickLeaveTranslations.modal.save.title(sickLeave.type),
                  message: sickLeaveTranslations.modal.save.message(sickLeave.type),
                  type: 'info',
                  secondMessage: sickLeaveTranslations.modal.save.secondMessage(sickLeave.type),
                  warning: false
                });
              }
            }}
          >
            {({ setFieldValue, values, errors }) => (
              <FormikForm className="sick-leave-form">
                <h5 className="mb-3 border-bottom pb-2">{sickLeaveTranslations.form.updateTitle(sickLeave.type, isEmployeeSick())}</h5>

                <div className="sick-leave-form-content">
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">{sickLeaveTranslations.form.endDate.label}</Form.Label>
                    <Field
                      type="date"
                      name="toDate"
                      className="form-control"
                      min={format(sickLeave.from, 'yyyy-MM-dd')}
                      disabled={sickLeave.type === 'employee' && originalSickLeave?.employee_submitted_data}
                    />
                    <ErrorMessage name="toDate" component="div" className="text-danger mt-1" />
                    <Form.Text className="text-muted">
                      {sickLeaveTranslations.form.endDate.help}
                    </Form.Text>
                  </Form.Group>

                  {sickLeave.type === 'employee' && sickLeave.reason === sickLeaveReasonEmployeeSick && (
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        id={`eAuFollows-${sickLeave.recordId}`}
                        label={sickLeaveTranslations.form.eAu.label}
                        name="eAuFollows"
                        checked={values.eAuFollows}
                        onChange={(e) => {
                          setFieldValue('eAuFollows', e.target.checked);
                        }}
                        className="user-select-none"
                        disabled={originalSickLeave?.employee_submitted_data}
                      />
                      <ErrorMessage name="eAuFollows" component="div" className="text-danger mt-1" />
                    </Form.Group>
                  )}

                  {sickLeave.type === 'employee' && ((sickLeave.reason === sickLeaveReasonEmployeeSick && !values.eAuFollows) ? (
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">{sickLeaveTranslations.form.sickNote.label(sickLeave.type, isEmployeeSick())}</Form.Label>
                        {sickLeave.sickLeaveNote && (
                          <p>{sickLeaveTranslations.form.sickNote.uploaded}</p>
                        )}
                        {!originalSickLeave?.employee_submitted_data && (
                          <>
                            <Form.Control
                              type="file"
                              size="sm"
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                  setFieldValue("sickNoteFile", e.currentTarget.files?.[0])
                              }
                            />
                            {errors.sickNoteFile && (
                              <div className="text-danger mt-1">
                                {errors.sickNoteFile.toString()}
                              </div>
                            )}
                            <Form.Text className="text-muted">
                              {sickLeaveTranslations.form.sickNote.fileFormat}
                            </Form.Text>
                          </>
                        )}
                      </Form.Group>
                    ) : null
                  )}
                </div>

                <div className="sick-leave-button-container mt-4">
                  <div className="d-flex gap-2">
                    <Button
                      variant="primary"
                      type="submit"
                      className="flex-grow-1"
                      onSubmit={(e) => {
                        setFinishClientSickLeave(false);
                        e.currentTarget.form?.requestSubmit()
                      }}
                    >
                      {sickLeaveTranslations.form.buttons.save}
                    </Button>
                    {sickLeave.type === 'client' && <Button
                        variant="secondary"
                        type={"submit"}
                        className="flex-grow-1"
                        onClick={(e) => {
                          setFinishClientSickLeave(true)
                          e.currentTarget.form?.requestSubmit()
                        }}
                    >
                      {sickLeaveTranslations.form.buttons.finish}
                    </Button>}
                  </div>
                </div>
              </FormikForm>
            )}
          </Formik>
        </Card.Body>
      </Card>

      {/* Bestätigungsmodal für Krankmeldungsupdate */}
      <Modal
          show={showConfirmModal !== null}
          onHide={() => {
            setShowConfirmModal(null);
            setFinishClientSickLeave(false)
          }}
          centered
          backdrop="static"
          size="lg"
      >
        <Modal.Header closeButton className={`${showConfirmModal?.warning ? 'bg-warning' : 'bg-primary'} text-white`}>
          <Modal.Title>{showConfirmModal?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4">
          <div className="d-flex align-items-center mb-3">
            <div className="fs-4 me-3">
              <i className="fas fa-info-circle text-primary"></i>
            </div>
            <div>
              <h5 className="mb-1">{showConfirmModal?.message}</h5>
              <p className="text-muted mb-0">{showConfirmModal?.secondMessage}</p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
              variant="outline-secondary"
              onClick={() => {
                setShowConfirmModal(null);
                setFinishClientSickLeave(false)
              }}
          >{sickLeaveTranslations.form.buttons.cancel}</Button>
          <Button
            variant={`${showConfirmModal?.warning ? 'warning' : 'primary'}`}
            onClick={confirmSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {sickLeaveTranslations.form.savingStatus}
              </>
            ) : (
              sickLeaveTranslations.form.buttons.save
            )}
          </Button>
        </Modal.Footer>
      </Modal>

    </CustomContainer>
  );
};

export default EditSingleSickLeave;
